import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppConfigService } from 'src/app/appConfig.service';

@Injectable({
  providedIn: 'root'
})
export class HubFactoryService {

  private _hubs = new Map<string, HubConnection>();
  private get baseUrl(): string {
    return this.configService.apiURL;
  }

  constructor(private configService: AppConfigService) { }

  createHub(hubName: string) {
    if (this._hubs.has(hubName)) {
      return of(this._hubs.get(hubName));
    }

    const hub = this._buildHub(hubName);
    this._hubs.set(hubName, hub);
    return of(hub);
  }

  private _buildHub(hubUrl: string) {
    return new HubConnectionBuilder()
      .withUrl(this._buildUrl(hubUrl), {
        // SD-1151: the transport is NOT pinned. It used to be forced to WebSockets, which meant
        // the client refused every other option — so wherever the server doesn't offer WebSockets
        // (Azure App Service has a per-app "Web sockets" toggle that is off by default), negotiate
        // succeeded, no transport could be agreed, and the connection silently never established.
        // Nothing surfaced that as an error: notifications simply never arrived, and the only
        // screens that appeared to work were the ones refreshing themselves locally.
        //
        // Left unset, SignalR negotiates the best transport the server actually advertises and
        // falls back through ServerSentEvents to LongPolling. WebSockets is still preferred when
        // available, so enabling it server-side remains the better outcome — this just stops the
        // absence of it from being fatal.
        withCredentials: true
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Error)
      .build();
  }

  private _buildUrl(hubUrl: string) {
    if (hubUrl.startsWith('http://') || hubUrl.startsWith('https://') || hubUrl.includes("/api/")) {
      return hubUrl;
    }
    return `${this.baseUrl}${hubUrl}`;
  }
}
